/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Why choose FalcoCut AI Image Translator section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Lightning-Fast Processing',
    description:
      'Speed matters when you need translations on the go. FalcoCut delivers results in seconds, helping you stay productive and focused. By cutting down waiting time, it makes image translation a seamless part of your workflow.',
  },
  {
    icon: '🌐',
    title: 'Easy Browser Access',
    description:
      'No downloads or installations are required. FalcoCut runs directly in your browser, making it simple to start translating images instantly. The intuitive interface ensures anyone can use it with ease, whether for quick tasks or professional projects.',
  },
  {
    icon: '🧩',
    title: 'Smart Review Tools',
    description:
      'FalcoCut provides clear translation previews that help you review and confirm results with confidence. Check accuracy, readability, and layout alignment before ensuring your translated images are ready to use without disrupting the original design.',
  },
  {
    icon: '🔒',
    title: 'Secure Data Protection',
    description:
      'Your data stays safe with FalcoCut. Advanced security measures protect images and translations, ensuring privacy at every step. You can translate sensitive documents or personal content with confidence, knowing your information is secure.',
  },
  {
    icon: '✨',
    title: 'Reliable Translation Accuracy',
    description:
      'Accuracy is at the core of FalcoCut AI image translator. It captures text clearly and translates it with precision, even across complex layouts. This reliability means you can trust the results for business, study, or everyday use.',
  },
  {
    icon: '💰',
    title: 'Affordable Translation for Everyone',
    description:
      'FalcoCut provides powerful translation features at a cost-effective rate. By combining professional-quality results with accessible pricing, it ensures individuals, teams, and businesses can benefit without overspending.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Image Translator
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut delivers fast, accurate, and secure image translation in your
          browser—saving time with a simple review-and-confirm workflow and dependable
          results. Simple to use from day one, with privacy-first processing you can
          trust.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
